import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";

type RecommandItem = {
  id: string;
  label: string;
  description?: string;
  imageUrl?: string;
};

type RecommandBoxProps = {
  value: number | null;
  className?: string;
  debounceMs?: number; // 입력 안정화 대기시간
  intervalMs?: number; // 다음 아이템으로 넘어가는 간격
  fadeMs?: number; // 크로스페이드 시간
  maxItems?: 1 | 2 | 3 | 4 | 5;
  items?: RecommandItem[]; // 외부에서 직접 주입 시 사용
  generateItems?: (value: number | null, maxItems: number) => RecommandItem[];
  renderItem?: (item: RecommandItem) => React.ReactNode;
  children?: React.ReactNode; // 하위 호환: children 배열을 그대로 순환하여 페이드
  style?: React.CSSProperties;
};

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function defaultGenerator(
  value: number | null,
  maxItems: number
): RecommandItem[] {
  const safeValue = typeof value === "number" && isFinite(value) ? value : 0;
  // value 범위에 따라 1~5개 구성 (결정적, SSR/CSR 동일)
  let count = 3;
  if (safeValue <= 0) count = 1;
  else if (safeValue < 300) count = 2;
  else if (safeValue < 1000) count = 3;
  else if (safeValue < 3000) count = 4;
  else count = 5;
  count = Math.max(1, Math.min(maxItems, count));

  return Array.from({ length: count }).map((_, idx) => ({
    id: `rec-${String(safeValue)}-${idx}`,
    label: `${idx + 1}번째 추천`,
    description:
      safeValue > 0
        ? `입력값 ${safeValue.toLocaleString()} 기준 추천 ${idx + 1}`
        : `시작을 위한 기본 추천 ${idx + 1}`,
  }));
}

function DefaultRenderer(item: RecommandItem) {
  return (
    <div className="w-full">
      <p className="text-gray90 text-[16px] leading-[150%] font-[600]">
        {item.label}
      </p>
      {item.description ? (
        <p className="text-gray70 text-[14px] leading-[150%] mt-1">
          {item.description}
        </p>
      ) : null}
    </div>
  );
}

function RecommandBox({
  value,
  className,
  debounceMs = 1000,
  intervalMs = 2200,
  fadeMs = 500,
  maxItems = 5,
  items,
  generateItems = defaultGenerator,
  renderItem = DefaultRenderer,
  children,
  style,
}: RecommandBoxProps) {
  const debouncedValue = useDebouncedValue(value, debounceMs);

  // children 배열을 그대로 순환하는 모드 (하위 호환)
  const childrenArray = useMemo(() => {
    if (!children) return null;
    const arr = Array.isArray(children) ? children : [children];
    return arr.filter(Boolean) as React.ReactNode[];
  }, [children]);

  const computedItems: RecommandItem[] = useMemo(() => {
    if (childrenArray && childrenArray.length > 0) {
      // children을 그대로 렌더; id는 안정적으로 생성
      return childrenArray.slice(0, maxItems).map((node, idx) => ({
        id: `child-${idx}`,
        label: `추천 ${idx + 1}`,
        description: undefined,
        // node 자체는 별도로 보관하지 않고 렌더 단계에서 childrenArray를 사용
      }));
    }
    if (items && items.length > 0) {
      return items.slice(0, maxItems);
    }
    return generateItems(debouncedValue, maxItems).slice(0, maxItems);
  }, [childrenArray, items, generateItems, debouncedValue, maxItems]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [phase, setPhase] = useState<"hold" | "fadeOut" | "fadeIn">("hold");
  const timersRef = useRef<number[]>([]);

  // 아이템 변경을 안정적으로 감지하기 위한 시그니처 (길이 동일 시에도 변화 감지)
  const itemsSignature = useMemo(
    () => computedItems.map((it) => it.id).join("|"),
    [computedItems]
  );

  // debouncedValue 또는 아이템 변경 시 초기화
  useEffect(() => {
    setActiveIndex(0);
  }, [debouncedValue, computedItems.length, itemsSignature]);

  // 자동 순환 (순차: hold -> fadeOut -> switch -> fadeIn)
  useEffect(() => {
    if (computedItems.length <= 1) return;

    const clearTimers = () => {
      timersRef.current.forEach((id) => clearTimeout(id));
      timersRef.current = [];
    };

    clearTimers();
    setPhase("hold");
    setActiveIndex(0);

    const schedule = () => {
      // 1) Hold 동안 유지
      const holdId = window.setTimeout(() => {
        setPhase("fadeOut");
        // 2) fadeOut 완료 후 인덱스 전환
        const fadeOutId = window.setTimeout(() => {
          setActiveIndex((prev) => (prev + 1) % computedItems.length);
          setPhase("fadeIn");
          // 3) fadeIn 완료 후 다음 사이클 시작
          const fadeInId = window.setTimeout(() => {
            setPhase("hold");
            schedule();
          }, fadeMs);
          timersRef.current.push(fadeInId);
        }, fadeMs);
        timersRef.current.push(fadeOutId);
      }, intervalMs);
      timersRef.current.push(holdId);
    };

    schedule();
    return () => clearTimers();
  }, [
    computedItems.length,
    itemsSignature,
    debouncedValue,
    intervalMs,
    fadeMs,
  ]);

  const renderContent = useCallback(
    (index: number) => {
      if (childrenArray && childrenArray.length > 0) {
        return childrenArray[index] ?? null;
      }
      const item = computedItems[index];
      if (!item) return null;
      return renderItem(item);
    },
    [childrenArray, computedItems, renderItem]
  );

  if (
    (childrenArray && childrenArray.length === 0) ||
    computedItems.length === 0
  ) {
    return null;
  }

  return (
    <div
      className={`border border-hairlineBorder rounded-[12px] px-4 py-2 ${
        className ?? ""
      }`}
      style={style}
    >
      <div className="relative">
        {/* 레이아웃 높이 확보용 보정 요소 (공간은 차지하되 보이지 않게) */}
        <div aria-hidden className="invisible">
          {renderContent(activeIndex)}
        </div>

        {/* 오버레이 크로스페이드 */}
        {computedItems.map((item, idx) => (
          <motion.div
            key={item.id}
            className="absolute inset-0"
            initial={{ opacity: idx === activeIndex ? 1 : 0 }}
            animate={{
              opacity: idx === activeIndex ? (phase === "fadeOut" ? 0 : 1) : 0,
            }}
            transition={{ duration: fadeMs / 1000 }}
            style={{
              pointerEvents:
                idx === activeIndex && phase !== "fadeOut" ? "auto" : "none",
            }}
          >
            {renderContent(idx)}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default RecommandBox;
